import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import ReceiptIcon      from "@mui/icons-material/Receipt";
import VisibilityIcon   from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import useListDateFilter from "hooks/useListDateFilter";
import { currencyFormatter }        from "utilities/currencyFormatter";
import { useGetCustomerInvoicesList } from "api/hooks/billing";

/* ── Status pill ── */
const StatusPill = ({ status }) => {
  const config = status === true
    ? { label: "Paid",      bg: "rgba(29,158,117,0.12)",  color: "#1D9E75" }
    : status === false
    ? { label: "Unpaid",    bg: "rgba(226,75,74,0.1)",    color: "#e24b4a" }
    : { label: "Cancelled", bg: "rgba(136,135,128,0.12)", color: "#888780" };

  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", borderRadius: "20px",
      backgroundColor: config.bg,
    }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: config.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: config.color, lineHeight: 1 }}>
        {config.label}
      </Typography>
    </Box>
  );
};

export default function CustomerInvoices() {
  const navigate = useNavigate();
  const theme    = useTheme();

  const bodyBg  = theme.palette.background.alt;
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    invoicesList, count, isLoadingInvoicesList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
  } = useGetCustomerInvoicesList(1, business_branch_id);

  const [search, setSearch] = useState("");

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const filtered = invoicesList.filter((r) =>
    r.invoice_number?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { label: "#",           width: 40  },
    { label: "Invoice No."             },
    { label: "Date Created"            },
    { label: "Amount",   align: "right"},
    { label: "Customer"                },
    { label: "Status"                  },
    { label: "Created By"              },
    { label: "Actions",  align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "0.5rem 0.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <ReceiptIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Invoices
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {count} total records
            </Typography>
          </Box>
        </Box>
      </Box>

      <ListFilterBar>
        <ListDateFilterBar
          embedded
          dateFilter={displayDateFilter}
          customRange={customRange}
          onFilterChange={handleFilterChange}
          onCustomRangeChange={setCustomRange}
          onCustomApply={handleCustomApply}
        />
        <ListSearchField placeholder="Search by invoice number…" onSearch={setSearch} />
      </ListFilterBar>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={filtered}
        loading={isLoadingInvoicesList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No invoices found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.invoice_number}
              </Typography>
            </RCell>
            <RCell>{moment(row.date_created).format("LLL")}</RCell>
            <RCell align="right">
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {currencyFormatter(parseFloat(row.total_amount))}
              </Typography>
            </RCell>
            <RCell>
              {row.related_customer
                ? `${row.related_customer.related_user.first_name} ${row.related_customer.related_user.last_name}`
                : <Typography sx={{ fontSize: "12px", color: textSec }}>—</Typography>}
            </RCell>
            <RCell><StatusPill status={row.status} /></RCell>
            <RCell>{`${row.created_by.first_name} ${row.created_by.last_name}`}</RCell>
            <RCell align="center">
              <Box
                onClick={() => navigate("/invoiceDetails", { state: { id: row.id } })}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  px: "8px", py: "4px", borderRadius: "6px",
                  border: "1px solid rgba(29,158,117,0.3)",
                  cursor: "pointer", color: accent,
                  fontSize: "12px", fontWeight: 500,
                  transition: "all 0.15s",
                  "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
                }}
              >
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View
              </Box>
            </RCell>
          </RRow>
        )}
      />
    </Box>
  );
}